import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
    type NavigateFunction,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';

import {
    fetchCatalogItems,
    fetchGlobalCatalogItems,
} from 'entities/CatalogItem';
import { GLOBAL_CATALOG_ITEMS_PAGE_SIZE } from 'entities/CatalogItem/model/consts/catalogItemsConsts';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { getSearchLocalCatalogItemValue } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteCatalog,
    getRouteCatalogListAddItemsCategories,
    getRouteCatalogListAddItemsMarketplace,
    getRouteGlobalCatalog,
    getRouteOrdersAddItemsCategories,
    getRouteOrdersAddItemsMarketplace,
} from 'shared/const/router';
import {
    useIsListsAddItemsFlow,
    useIsOrdersAddItemsFlow,
} from 'shared/lib/contexts/CatalogIdentityContext';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';

import type { AppDispatch } from 'app/providers/StoreProvider';

type NavigationType = 'global-marketplace' | 'property-catalog';

type FetchWithControllerFn = (
    fetchFn: (controller: AbortController) => Promise<unknown>,
) => void;

function navigateToGlobalMarketplace(args: {
    dispatch: AppDispatch;
    navigate: NavigateFunction;
    fetchWithController: FetchWithControllerFn;
    localSearchValue: string;
    propertyId: string | undefined;
    listId: string | undefined;
    isOrdersAddItemsFlow: boolean;
    isListsAddItemsFlow: boolean;
    locationState: unknown;
    clearSearch?: boolean;
}) {
    const {
        dispatch,
        navigate,
        fetchWithController,
        localSearchValue,
        propertyId,
        listId,
        isOrdersAddItemsFlow,
        isListsAddItemsFlow,
        locationState,
        clearSearch,
    } = args;

    if (clearSearch) {
        dispatch(catalogItemActions.clearAllItems());
        dispatch(searchCatalogItemsActions.setActive(false));
    } else {
        if (localSearchValue) {
            dispatch(searchCatalogItemsActions.setSkipReset(true));
        }
        dispatch(searchCatalogItemsActions.setActive(true));
    }
    dispatch(addCatalogItemActions.setEditItemMode(true));
    dispatch(
        searchCatalogItemsActions.setGlobalCatItemSearchValue(localSearchValue),
    );
    dispatch(categoryActions.clearCategorySelection());

    if (isOrdersAddItemsFlow) {
        navigate(getRouteOrdersAddItemsMarketplace(), {
            state: {
                ...((locationState ?? {}) as object),
                autoFocusSearch: clearSearch,
            },
        });
    } else if (isListsAddItemsFlow && propertyId && listId) {
        navigate(getRouteCatalogListAddItemsMarketplace(propertyId, listId), {
            state: { autoFocusSearch: clearSearch },
        });
    } else if (propertyId) {
        navigate(getRouteGlobalCatalog(propertyId), {
            state: { autoFocusSearch: clearSearch },
        });
    }

    if (localSearchValue) {
        fetchWithController((controller: AbortController) =>
            dispatch(
                fetchGlobalCatalogItems({
                    searchString: localSearchValue,
                    page: 1,
                    pageSize: GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                    propertyId,
                    abortController: controller,
                }),
            ),
        );
    }
}

function navigateToPropertyCatalog(args: {
    dispatch: AppDispatch;
    navigate: NavigateFunction;
    fetchWithController: FetchWithControllerFn;
    localSearchValue: string;
    propertyId: string | undefined;
    listId: string | undefined;
    catalogId: string | undefined;
    isOrdersAddItemsFlow: boolean;
    isListsAddItemsFlow: boolean;
    locationState: unknown;
}) {
    const {
        dispatch,
        navigate,
        fetchWithController,
        localSearchValue,
        propertyId,
        listId,
        catalogId,
        isOrdersAddItemsFlow,
        isListsAddItemsFlow,
        locationState,
    } = args;

    if (localSearchValue) {
        dispatch(searchCatalogItemsActions.setSkipReset(true));
        dispatch(searchCatalogItemsActions.setActive(true));
        dispatch(
            searchCatalogItemsActions.setLocalCatItemSearchValue(
                localSearchValue,
            ),
        );
        dispatch(catalogItemActions.setLoading(true));
    }

    if (isOrdersAddItemsFlow) {
        navigate(getRouteOrdersAddItemsCategories(), {
            state: locationState,
        });
    } else if (isListsAddItemsFlow && propertyId && listId) {
        navigate(getRouteCatalogListAddItemsCategories(propertyId, listId));
    } else if (propertyId) {
        navigate(getRouteCatalog(propertyId));
    }

    if (localSearchValue && catalogId) {
        fetchWithController((controller: AbortController) =>
            dispatch(
                fetchCatalogItems({
                    data: {
                        catalogId,
                        searchStr: localSearchValue,
                        isInventory: false,
                    },
                    abortController: controller,
                }),
            ),
        );
    }
}

export const useMarketplaceNavigation = (
    type: NavigationType = 'global-marketplace',
) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const isOrdersAddItemsFlow = useIsOrdersAddItemsFlow();
    const isListsAddItemsFlow = useIsListsAddItemsFlow();
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const selectedPropertyId = useSelector(getSelectedPropertyId);
    const fetchWithController = useFetchWIthController();

    const navigateToMarketplace = useCallback(
        (options?: { clearSearch?: boolean }) => {
            const propertyId =
                params.propertyId ||
                location.state?.selectedPropertyId ||
                selectedPropertyId;
            const listId = params.listId;
            const searchValue = options?.clearSearch ? '' : localSearchValue;

            const branchArgs = {
                dispatch,
                navigate,
                fetchWithController,
                localSearchValue: searchValue,
                propertyId,
                listId,
                isOrdersAddItemsFlow,
                isListsAddItemsFlow,
                locationState: location.state,
                clearSearch: options?.clearSearch,
            };

            if (type === 'global-marketplace') {
                navigateToGlobalMarketplace(branchArgs);
            } else {
                navigateToPropertyCatalog({
                    ...branchArgs,
                    catalogId,
                });
            }
        },
        [
            dispatch,
            navigate,
            params.propertyId,
            params.listId,
            localSearchValue,
            catalogId,
            selectedPropertyId,
            fetchWithController,
            type,
            isOrdersAddItemsFlow,
            isListsAddItemsFlow,
            location.state,
        ],
    );

    return { navigateToMarketplace };
};
